import { useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { useManufacturedDevices } from "../../../../hooks/useManufacturedDevices"
import { digitalManagerApi } from "../../../../services/api"
import { ManufacturedDeviceGeneralInfo } from "../../../models/SupplierPortal"
import { getDoneIcon, getEmptyIcon } from "../../../utils/getBooleanFlagIcon"
import {
  dateStringComparator,
  getNiceFormattedDate,
} from "../../../utils/getFormattedDate"
import { EntityRow, EntityTab } from "../../partials/EntityTab"

export function ManufacturedDevices() {
  const {
    manufacturedDevicesList,
    isLoadingManufacturedDevice,
    reloadManufacturedDevices,
  } = useManufacturedDevices()
  const navigate = useNavigate()

  const onValidate = useCallback(
    (row: EntityRow) => {
      digitalManagerApi
        .put(`/litePanelPro/resetPassword?manufacturedDeviceId=${row.id}`)
        .then(() => {
          toast.success("Reset password request validated successfully")
          reloadManufacturedDevices()
        })
        .catch(() => {
          toast.error("Failed to validate the request")
        })
    },
    [reloadManufacturedDevices]
  )

  const onDetails = useCallback(
    (row: EntityRow) => {
      navigate(`/supplierPortal/manufacturedDevices/${row.id}`)
    },
    [navigate]
  )

  const cols = [
    { field: "serialNumberAbb", headerName: "SN ABB" },
    { field: "serialNumberAsem", headerName: "SN ASEM" },
    { field: "wanMac", headerName: "Wan Mac" },
    { field: "lanMac", headerName: "Lan Mac" },
    {
      field: "manufacturingDate",
      headerName: "Manufacturing Date",
      sortComparator: dateStringComparator,
    },
    { field: "status", headerName: "Status" },
    {
      field: "resetPasswordPending",
      headerName: "Reset Password Pending",
      sortComparator: (a: string, b: string) => Number(a) - Number(b),
      renderCell: (row: EntityRow) =>
        row.resetPasswordPending ? getDoneIcon() : getEmptyIcon(),
    },
  ]

  const rows = manufacturedDevicesList.map((d: ManufacturedDeviceGeneralInfo) => ({
    id: d.id,
    serialNumberAbb: d.serialNumberAbb,
    serialNumberAsem: d.serialNumberAsem,
    wanMac: d.wanMac,
    lanMac: d.lanMac,
    manufacturingDate: getNiceFormattedDate(d.manufacturingDate),
    status: d.status,
    resetPasswordPending: d.litePanelProResetPasswordRequests.some(
      (rpr) => rpr.completionDate == null
    )
      ? 1
      : 0,
    canValidate: d.litePanelProResetPasswordRequests.some(
      (rpr) => rpr.completionDate == null
    ),
  }))

  const handleActionMenuClick = useCallback(
    (item: { title: string; iconName?: string }, row: EntityRow) => {
      if (item.title === "Details") {
        onDetails(row)
      }
      if (item.title === "Validate" && row.canValidate) {
        onValidate(row)
      }
    },
    [onDetails, onValidate]
  )

  return (
    <EntityTab
      rows={rows}
      cols={cols}
      isLoading={isLoadingManufacturedDevice}
      actionItems={[
        { title: "Details", iconName: "icon_abb_dias icon-abb_system-detailsy" },
        { title: "Validate", iconName: "icon_abb_dias icon-abb_Task-List-Check-2" },
      ]}
      onActionMenuClick={handleActionMenuClick}
    />
  )
}
